import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { cn } from '@mdvip/shared/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-full border border-transparent bg-clip-padding text-sm font-semibold whitespace-nowrap transition-[color,background-color,border-color,box-shadow] duration-fast ease-standard outline-none select-none active:brightness-[0.97] focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-2 aria-invalid:border-destructive-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_i]:pointer-events-none [&_i]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [@media(pointer:coarse)]:min-h-[44px]",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
        outline:
          'border-border-control-soft bg-background shadow-xs hover:border-input hover:bg-accent hover:text-accent-foreground aria-expanded:bg-accent aria-expanded:text-accent-foreground dark:border-border-control-soft dark:bg-input/30 dark:hover:border-input dark:hover:bg-input/50',
        secondary:
          'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground',
        ghost:
          'hover:bg-accent hover:text-accent-foreground aria-expanded:bg-accent aria-expanded:text-accent-foreground dark:hover:bg-accent/50',
        destructive:
          'bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        link: 'text-foreground no-underline hover:text-primary [&>*:not(svg):not(i)]:underline [&>*:not(svg):not(i)]:decoration-from-font [&>*:not(svg):not(i)]:underline-offset-2',
      },
      size: {
        default:
          'h-12 gap-2 px-6 text-base leading-6 in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-5 has-data-[icon=inline-start]:pl-5',
        xs: "h-6 gap-1 px-2 text-xs in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: 'h-8 gap-1.5 px-4 text-xs leading-4 in-data-[slot=button-group]:rounded-md',
        lg: "h-14 gap-2 px-8 text-lg leading-6 has-data-[icon=inline-end]:pr-6 has-data-[icon=inline-start]:pl-6 [&_svg:not([class*='size-'])]:size-5",
        icon: 'size-9 [@media(pointer:coarse)]:min-w-[44px]',
        'icon-xs':
          "size-6 in-data-[slot=button-group]:rounded-md [&_svg:not([class*='size-'])]:size-3 [@media(pointer:coarse)]:min-w-[44px]",
        'icon-sm': 'size-8 in-data-[slot=button-group]:rounded-md [@media(pointer:coarse)]:min-w-[44px]',
        'icon-lg': 'size-10 [@media(pointer:coarse)]:min-w-[44px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function linkChildren(children: React.ReactNode): React.ReactNode {
  return React.Children.map(children, (child) =>
    typeof child === 'string' || typeof child === 'number' ? (
      <span className="underline decoration-from-font underline-offset-2">{child}</span>
    ) : (
      child
    ),
  );
}

function Button({
  className,
  variant = 'default',
  size = 'default',
  children,
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
  return (
    <ButtonPrimitive data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props}>
      {variant === 'link' ? linkChildren(children as React.ReactNode) : (children as React.ReactNode)}
    </ButtonPrimitive>
  );
}

export { Button, buttonVariants };
